import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";

export const OCR_SCOPE_CHANGED = "The session or business unit changed. Close this inbox and select the correct business unit before reopening it.";
export const isOcrScopeError = (error) => error?.code === "OCR_BUSINESS_UNIT_CHANGED" || error?.response?.data?.code === "OCR_BUSINESS_UNIT_CHANGED";

// Each opening owns one user/BU. A different tab may replace the shared JWT,
// but must never silently redirect this inbox or its draft to another BU.
export default function useOcrBusinessUnitScope(isOpen, onInvalidate) {
  const [searchParams] = useSearchParams();
  const routeBu = searchParams.has("business_unit_id") ? Number(searchParams.get("business_unit_id")) : null;
  const routeRef = useRef(routeBu);
  routeRef.current = routeBu;
  const openRef = useRef(false);
  const mountedRef = useRef(true);
  const scopeRef = useRef(null);
  const invalidRef = useRef(null);
  const invalidateCallback = useRef(onInvalidate);
  invalidateCallback.current = onInvalidate;
  const [, setInvalidatedScope] = useState(null);
  if (isOpen && !openRef.current) {
    const user = readStoredUser();
    scopeRef.current = { userId: user?.id, businessUnitId: routeBu ?? Number(user?.active_business_unit_id) };
  }
  openRef.current = isOpen;
  const scope = scopeRef.current;

  const isCurrent = useCallback((snapshot) => {
    const user = readStoredUser();
    return Boolean(snapshot && mountedRef.current && openRef.current && scopeRef.current === snapshot && invalidRef.current !== snapshot &&
      snapshot.userId && String(user?.id) === String(snapshot.userId) &&
      Number.isInteger(snapshot.businessUnitId) && snapshot.businessUnitId > 0 &&
      Number(user?.active_business_unit_id) === snapshot.businessUnitId &&
      (routeRef.current === null || routeRef.current === snapshot.businessUnitId));
  }, []);
  const invalidate = useCallback((snapshot) => {
    if (!mountedRef.current || !snapshot || scopeRef.current !== snapshot || invalidRef.current === snapshot) return;
    invalidRef.current = snapshot;
    setInvalidatedScope(snapshot);
    invalidateCallback.current?.();
  }, []);
  const check = useCallback((snapshot) => {
    if (isCurrent(snapshot)) return;
    invalidate(snapshot);
    throw Object.assign(new Error(OCR_SCOPE_CHANGED), { code: "OCR_BUSINESS_UNIT_CHANGED" });
  }, [invalidate, isCurrent]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onSessionChange = () => { if (!isCurrent(scope)) invalidate(scope); };
    onSessionChange();
    window.addEventListener("storage", onSessionChange);
    window.addEventListener("palantir-session-updated", onSessionChange);
    return () => {
      window.removeEventListener("storage", onSessionChange);
      window.removeEventListener("palantir-session-updated", onSessionChange);
    };
  }, [isOpen, routeBu, scope, isCurrent, invalidate]);

  const api = useMemo(() => {
    const request = async (method, url, body, config) => {
      check(scope);
      const options = { ...config, pilcoreExpectedUserId: scope.userId, headers: { ...config?.headers, "X-Pilcore-Business-Unit-Id": scope.businessUnitId } };
      try {
        const response = await (method === "get" || method === "delete" ? axios[method](url, options) : axios[method](url, body, options));
        check(scope);
        return response;
      } catch (error) {
        if (isOcrScopeError(error)) invalidate(scope);
        if (!isCurrent(scope)) throw Object.assign(new Error(OCR_SCOPE_CHANGED), { code: "OCR_BUSINESS_UNIT_CHANGED" });
        throw error;
      }
    };
    return {
      get: (url, config) => request("get", url, undefined, config),
      delete: (url, config) => request("delete", url, undefined, config),
      post: (url, body, config) => request("post", url, body, config),
      patch: (url, body, config) => request("patch", url, body, config),
      put: (url, body, config) => request("put", url, body, config),
      isCurrent: () => isCurrent(scope),
    };
  }, [scope, check, invalidate, isCurrent]);
  return { api, businessUnitId: scope?.businessUnitId, user: readStoredUser(), blocked: isOpen && !isCurrent(scope) };
}
